/**
 * Finance — domain shapes + versioned vocabulary (FINAPP-1, ADR 0076).
 *
 * Types + const enums + versioned event names only (the domain-file convention; runtime
 * parsers live in the owning service, and the shared WIRE zod lives in `http/finance-v1.ts`).
 * The finance service renders SquadLab's finances read straight from a Google Sheet — it owns
 * no database, so these shapes describe a point-in-time SNAPSHOT of the sheet, not stored rows.
 * Amounts are plain numbers in the snapshot's `currency`; timestamps are epoch ms (injected clock).
 */

/** Schema version (bump on any breaking domain-shape change; never mutate in place). */
export const FINANCE_SCHEMA_VERSION = 1 as const;

/** Where a snapshot's figures came from — surfaced so the UI/MCP can be honest about simulation. */
export const FINANCE_SOURCE_KINDS = ['mock', 'google-sheet'] as const;
export type FinanceSourceKind = (typeof FINANCE_SOURCE_KINDS)[number];

/** How to render a KPI value. */
export const KPI_UNITS = ['currency', 'months', 'percent', 'number'] as const;
export type KpiUnit = (typeof KPI_UNITS)[number];

/** Whether an increase is good (revenue) or bad (burn) — drives the delta colour. */
export const KPI_DIRECTIONS = ['up-good', 'down-good', 'neutral'] as const;
export type KpiDirection = (typeof KPI_DIRECTIONS)[number];

/** A headline metric card: cash, runway, monthly burn, revenue, net, … */
export interface FinanceKpi {
  /** Stable key, e.g. 'cash' | 'runway' | 'burn' | 'revenue' | 'net'. */
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly unit: KpiUnit;
  /** Change vs the prior period as a fraction (0.12 = +12%), when known. */
  readonly deltaPct?: number | undefined;
  readonly direction?: KpiDirection | undefined;
  readonly note?: string | undefined;
}

/** A P&L / cash-flow line's bucket. Category carries the sign convention (expense magnitudes are positive). */
export const PL_CATEGORIES = ['revenue', 'expense', 'other'] as const;
export type PlCategory = (typeof PL_CATEGORIES)[number];

/** One line of the profit-and-loss / cash-flow statement. */
export interface FinancePlLine {
  readonly id: string;
  readonly label: string;
  readonly category: PlCategory;
  /** Amount in the snapshot's currency. Revenue/other signed as given; expense stored as a positive magnitude. */
  readonly amount: number;
  readonly note?: string | undefined;
}

/** One point on a trend series (e.g. a month). */
export interface FinanceTrendPoint {
  /** Period label, e.g. '2026-01' or 'Jan'. */
  readonly period: string;
  readonly value: number;
}

/** A named time series for the trend chart (revenue, expenses, net, cash runway, …). */
export interface FinanceSeries {
  readonly key: string;
  readonly label: string;
  readonly points: readonly FinanceTrendPoint[];
}

/**
 * A point-in-time snapshot of the finances — the single payload the console renders and the MCP
 * tools read. `source` is the honesty flag ('mock' = simulation). `asOf` is when the figures
 * reflect; `generatedAt` is when this snapshot was produced (cache-through the sheet).
 */
export interface FinanceSnapshot {
  readonly ownerId: string;
  /** Entity / sheet label, e.g. 'SquadLab OS'. */
  readonly title: string;
  /** ISO 4217 currency code, e.g. 'USD' | 'SAR'. */
  readonly currency: string;
  readonly source: FinanceSourceKind;
  readonly asOf: number;
  readonly generatedAt: number;
  readonly kpis: readonly FinanceKpi[];
  readonly pl: readonly FinancePlLine[];
  readonly series: readonly FinanceSeries[];
  readonly notes?: string | undefined;
}

/** Versioned domain-event names (emitted with a correlation id; domain-significant outcomes only). */
export const FINANCE_EVENTS = {
  snapshotRefreshed: 'finance.snapshot.refreshed.v1',
} as const;
export type FinanceEventName = (typeof FINANCE_EVENTS)[keyof typeof FINANCE_EVENTS];
